import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { uniqueId } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
  getFieldNamesFromColumnConfig,
} from '~/components/SolaForceDataGrid';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { WorktimeReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';
import { getFormattedDateString } from '~/utils/helpers/date';
import { getEnumObject } from '~/utils/helpers/enum';

import { useGetCustomFieldValue } from '../WorktimeReport/useGetCustomFieldValue';

type WorktimeDimensionReportEntry = {
  empId: number;
  empFirstName: string;
  empLastName: string;
  empNumber: string;
  empPosition: string;
  empUnitName: string;
  empUnitNumber: string;
  country: string;
  legalEntityCode: string;
  legalEntityName: string;
  costCenterCode: string;
  costCenterName: string;
  date: string;
  reportingType: string;
  sum: string;
  uniqueId: string;
};

type DimensionFilters = {
  date?: string;
  reportingType?: Enum;
  customFields?: Enum[];
};

type PropsType = {
  data: WorktimeReportData[];
  filters?: DimensionFilters;
};

export const useWorktimeDimensionReport = (props: PropsType) => {
  const { data, filters } = props;

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { t } = useTranslation();
  const { getEnums } = useAllEnums();
  const { getCustomFieldValue } = useGetCustomFieldValue();

  const convertToMinutes = (h: string) =>
    parseInt(h.split(':')[0], 10) * 60 + parseInt(h.split(':')[1], 10);

  const sumConverter = (sumInH: number) =>
    `${sumInH / 60 < 10 && sumInH / 60 > -10 ? '0' : ''}${
      (sumInH / 60).toString().split('.')[0]
    }:${sumInH % 60 < 10 && sumInH % 60 > -10 ? '0' : ''}${sumInH % 60}`;

  const rowsData = useMemo(() => {
    if (
      !filters?.date &&
      !filters?.reportingType &&
      !filters?.customFields?.length
    ) {
      return [];
    }
    return data
      .filter((entry) => !!entry.fEmpTimeBlocks?.length)
      .reduce((acc, entry) => {
        const {
          fEmpId,
          fEmpFirstName,
          fEmpLastName,
          fEmpNumber,
          fEmpPosition,
          fEmpUnitName,
          fEmpUnitNumber,
          fEmpOfficeCountry,
          fEmpLegalEntityCode,
          fEmpLegalEntityName,
          fEmpCostCenterCode,
          fEmpCostCenterName,
          fEmpTimeBlocks,
        } = entry;

        const customFields =
          entry.fEmpTimeBlocks?.flatMap((block) => block.customData || []) ||
          [];

        // Filter time blocks based on provided filters
        let filteredBlocks = fEmpTimeBlocks;

        if (filters?.date) {
          filteredBlocks = filteredBlocks.filter(
            (block) =>
              block.entryDate ===
              getFormattedDateString(
                filters.date ? new Date(filters.date) : undefined,
                'YYYY-MM-DD',
              ),
          );
        }

        if (filters?.reportingType) {
          filteredBlocks = filteredBlocks.filter(
            (block) => block.reportingType === filters.reportingType?.code,
          );
        }

        if (filters?.customFields) {
          filters.customFields.forEach((customField) => {
            filteredBlocks = filteredBlocks.filter((block) =>
              block.customData?.some(
                (data) => data.fCustomDataFieldName === customField.code,
              ),
            );
          });
        }

        // Calculate sum of hours for filtered blocks
        const dimensionSum = filteredBlocks
          .reduce((sum, block) => {
            const hours = convertToMinutes(block.hours || '0');
            return sum + hours;
          }, 0)
          .toString();

        if (dimensionSum === '0') {
          return acc;
        }

        return [
          ...acc,
          {
            uniqueId: uniqueId(),
            empId: fEmpId,
            empFirstName: fEmpFirstName,
            empLastName: fEmpLastName,
            empNumber: fEmpNumber,
            empPosition: fEmpPosition,
            empUnitName: fEmpUnitName,
            empUnitNumber: fEmpUnitNumber,
            country: fEmpOfficeCountry,
            legalEntityCode: fEmpLegalEntityCode,
            legalEntityName: fEmpLegalEntityName,
            costCenterCode: fEmpCostCenterCode,
            costCenterName: fEmpCostCenterName,
            date: filters?.date || '',
            reportingType: filters?.reportingType?.name || '',
            sum: sumConverter(parseInt(dimensionSum, 10)),
            ...customFields.reduce((acc, customField) => {
              acc[customField.fCustomDataFieldName] =
                customField.fCustomDataFieldValue;
              return acc;
            }, {} as Record<string, string[]>),
          },
        ];
      }, [] as WorktimeDimensionReportEntry[]);
  }, [data, filters]);

  const summaryHours = useMemo(() => {
    return sumConverter(
      rowsData.reduce((sum, row) => sum + convertToMinutes(row.sum), 0),
    );
  }, [rowsData]);

  const customDataColumns = data
    .filter((entry) => !!entry.fEmpTimeBlocks?.length)
    .flatMap((entry) => entry.fEmpTimeBlocks || [])
    .flatMap((block) => block.customData || [])
    .reduce((acc, entity) => {
      if (acc[entity.fCustomDataFieldName]) {
        return acc;
      }
      return {
        ...acc,
        [entity.fCustomDataFieldName]: {
          field: entity.fCustomDataFieldName,
          headerName: entity.fCustomDataFieldName,
          valueGetter: () => {
            return getCustomFieldValue(
              entity,
              getEnumObject(
                getEnums(EnumListKey.CUSTOM_ENTITY_TIMESHEET),
                entity.fCustomDataFieldName,
              ),
            );
          },
          minWidth: 100,
        },
      };
    }, {} as { [key: string]: GridColDef<WorktimeDimensionReportEntry> });

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const employeeColumnConfigs: GridColDef<WorktimeDimensionReportEntry>[] = [
      {
        field: 'empId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.empId);
              }}
            />
          );
        },
        minWidth: 60,
      },
      {
        field: 'empFirstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.empId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'empLastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.empId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'empNumber',
        headerName: t('laEmpId'),
        minWidth: 100,
      },
      {
        field: 'empUnitName',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      {
        field: 'empUnitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 120,
      },
      {
        field: 'legalEntityCode',
        headerName: t('laLegalEntityCode'),
        minWidth: 120,
      },
      {
        field: 'legalEntityName',
        headerName: t('laLegalEntityName'),
        minWidth: 150,
      },
      {
        field: 'costCenterName',
        headerName: t('laCostCenterCode'),
        minWidth: 150,
      },
      {
        field: 'costCenterCode',
        headerName: t('laCostCenter'),
        minWidth: 100,
      },
    ];

    const worktimeColumnConfigs: GridColDef<WorktimeDimensionReportEntry>[] = [
      {
        field: 'date',
        headerName: t('laDate'),
        minWidth: 100,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'reportingType',
        headerName: t('laReportingType'),
        minWidth: 150,
      },
      {
        field: 'sum',
        headerName: t('sum'),
        minWidth: 100,
      },
      ...Object.values(customDataColumns),
    ];

    const columnConfigs: GridColDef<WorktimeDimensionReportEntry>[] = [
      ...employeeColumnConfigs,
      ...worktimeColumnConfigs,
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'worktime',
        headerName: t('laWorktime'),
        children: getFieldNamesFromColumnConfig(worktimeColumnConfigs),
      },
    ];

    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [t, goToEmployeeBasicDataView, getEnums]);

  return {
    table: {
      columnConfigs,
      rows: rowsData,
      columnGroupingModel,
      summaryHours,
    },
  };
};
